import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Chip, Divider, Typography } from "@material-ui/core";
import {
  CallState,
  CorpSessionInfo,
  ListingCollectionEnum,
  LodgingCollectionEnum,
  RewardsAccount,
  TokenizeCardErrors,
  TravelProductEnum,
} from "redmond";
import {
  B2BPaymentMethodSelectWorkflow,
  B2BSpinner,
  BannerSeverity,
  CheckoutPaymentForm,
  createElementId,
  GenericInfoPopup,
  getHSPEarnOfferDescription,
  getPaymentCardSubtitle,
  Icon,
  IconName,
  LoadingIndicator,
  LoadingPopup,
  NotificationBanner,
  PoweredByHopper,
  TEST_CARD_LAST_FOURS,
  useDeviceTypes,
  usePrevious,
  paymentTextConstants,
  PrefillPaymentFormData,
  Tooltip,
  SanitizedHtml,
} from "halifax";
import clsx from "clsx";
import { useExperimentIsVariant } from "@capone/experiments";
import { RouteComponentProps } from "react-router";
import {
  isCaponeTenant,
  isCorpTenant,
  isVesperTenant,
  getConfigTenant,
} from "@capone/common";
import { shouldAutoSelectCard } from "@capone/common/src/utils/shouldAutoSelectCard";

import "./styles.scss";
import { RewardsSelection } from "../RewardsSelection";
import { PaymentCardConnectorProps } from "./container";
import {
  PAYMENT_PAY_LATER_NOTES,
  PC_PAYMENT_TYPES_ELIGIBILITY_BANNER_TEXT,
  PAYMENT_STEP_PAY_LATER_SUBTITLE,
  PAYMENT_STEP_PAY_LATER_TITLE_MOBILE,
  PAYMENT_STEP_PAY_LATER_TITLE,
} from "./textConstants";
import { PREMIER_COLLECTION_REWARDS_DISABLED_BANNER_TEXT } from "../../../rewards/components/RewardsBanner/constants";
import { config } from "../../../../api/config";
import {
  ALL_CARDS_IN_PHC,
  ANNUAL_TRAVEL_CREDITS,
  AVAILABLE,
  C1_AUTO_SELECT_CARD_AT_CHECKOUT,
  CONTROL,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  LC_GATING_EXPERIMENT,
  PREMIER_COLLECTION_EXPERIMENT,
  TRAVEL_SALE,
  TRAVEL_SALE_VARIANTS,
  useExperiments,
  VCN_ENABLEMENT,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { TravelWalletSelection } from "../TravelWalletSelection";
import { ClientContext } from "../../../../App";

export interface IPaymentCardProps
  extends PaymentCardConnectorProps,
    RouteComponentProps {
  disabled?: boolean;
  className?: string;
}

export const shouldDisableCard =
  (
    canUseAllCards: boolean,
    isLifestyleCollection?: boolean,
    isLifestyleEligible?: boolean | null,
  ) =>
  (rewardsAccount: RewardsAccount) => {
    if (canUseAllCards) {
      return false;
    }

    // Special check for Spark Cash Plus on corporate - requires lifestyle eligibility
    if (
      isCorpTenant(config.TENANT) &&
      rewardsAccount.productDisplayName === "Spark Cash Plus"
    ) {
      return !(isLifestyleCollection && isLifestyleEligible);
    }

    const allowedProducts =
      isCaponeTenant(config.TENANT) || isVesperTenant(config.TENANT)
        ? [
            ...(isLifestyleCollection ? ["Venture", "Spark Miles"] : []),
            "Venture X",
            "Spark Travel Elite",
            "Venture X Business",
            "Vesper",
          ]
        : [
            "Venture X Business",
            "Vesper",
            ...(isLifestyleCollection
              ? ["Venture Business", "Spark Miles"]
              : []),
          ];
    const isInDisplayName = (product: string) =>
      isCaponeTenant(config.TENANT) || isVesperTenant(config.TENANT)
        ? rewardsAccount.productDisplayName.includes(product) &&
          rewardsAccount.productDisplayName !== "VentureOne" &&
          !rewardsAccount.productDisplayName.includes("Select")
        : rewardsAccount.productDisplayName === product;
    const isCardEnabled = allowedProducts.some(isInDisplayName);
    return !isCardEnabled;
  };

const PaymentAddingElement = () => (
  <LoadingPopup
    indicatorSize="small"
    indicator={B2BSpinner}
    open
    popupSize="small"
    message="Adding your payment method"
    footer={PoweredByHopper}
  />
);

export const PaymentCard = ({
  listPaymentMethods,
  verifyPaymentMethod,
  deletePaymentMethod,
  setSelectedPaymentMethodId,
  fetchRewardsAccounts,
  fetchProductToEarn,
  fetchAllEarnForProduct,
  selectedRewardsPaymentAccount,
  selectedRewardsPaymentAccountId,
  isCreditCardPaymentRequired,
  totalCreditCardPaymentRequired,
  paymentMethods,
  rewardsAccounts,
  selectedPaymentMethodId,
  listPaymentMethodCallState,
  verifyPaymentMethodCallState,
  deletePaymentMethodCallState,
  disabled = false,
  hasError,
  className,
  isTravelCreditPaymentOnly,
  isStackedTravelWalletPaymentOnly,
  isTravelWalletOfferPaymentOnly,
  selectedLodging,
  selectedHome,
  canRedeemRewards,
  offerToApply,
  earnValuesByRewardAcctId,
  isPayLater,
  hasPayLater,
  isLifestyleEligible,
  credits,
  filteredOffers,
  walletLoaded,
}: IPaymentCardProps) => {
  const { matchesMobile } = useDeviceTypes();
  const { sessionInfo, isRewardsDisabled } = useContext(ClientContext);
  const isVesper = isVesperTenant(getConfigTenant());
  const [openErrorPaymentModal, setOpenErrorPaymentModal] = useState(false);
  const [isNotCapOneAccount, setIsNotCapOneAccount] = useState(false);
  const [tokenizeErrors, setTokenizeErrors] = useState<TokenizeCardErrors[]>(
    [],
  );
  const [paymentFormPrefillData, setPaymentFormPrefillData] =
    useState<PrefillPaymentFormData | null>(null);

  const corpSessionInfo = sessionInfo && (sessionInfo as CorpSessionInfo);

  useEffect(() => {
    listPaymentMethods();
    fetchRewardsAccounts(true, sessionInfo, true);
  }, []);

  const expState = useExperiments();

  const autoSelectExperimentOn =
    getExperimentVariant(
      expState.experiments,
      C1_AUTO_SELECT_CARD_AT_CHECKOUT,
    ) === AVAILABLE;

  useEffect(() => {
    const toSelect = shouldAutoSelectCard({
      paymentMethods,
      filteredOffers,
      credits,
      selectedPaymentMethodId,
      experimentOn: autoSelectExperimentOn,
      walletLoaded,
    });
    if (!toSelect) return;
    const card = paymentMethods[0];
    const matchingRewardsAccount = rewardsAccounts.find(
      (account) =>
        account.lastFour === card.last4 ||
        (card.last4 &&
          account.lastFourVirtualCardNumbers?.includes(card.last4)),
    );
    setSelectedPaymentMethodId({
      paymentMethodId: toSelect,
      accountId: matchingRewardsAccount?.accountReferenceId,
    });
  }, [
    paymentMethods,
    filteredOffers,
    credits,
    autoSelectExperimentOn,
    walletLoaded,
    rewardsAccounts,
    setSelectedPaymentMethodId,
  ]);

  const previousOfferToApply = usePrevious(offerToApply);

  useEffect(() => {
    const offer = offerToApply || previousOfferToApply;

    const hspEarnDescription = getHSPEarnOfferDescription(offer);

    // re-fetch product to earn when toggling HSP earn offer to get correct earn
    if (hspEarnDescription) {
      fetchAllEarnForProduct();
    }
  }, [offerToApply]);

  const ineligibleRewardsAccounts = useMemo(
    () =>
      rewardsAccounts.filter(
        (account) => !(account.allowRewardsRedemption ?? true),
      ),
    [rewardsAccounts],
  );

  const handleOnAddPaymentMethod = (token: string, last4: string) => {
    // note: cap1 specific logic:
    // A card should be deemed ineligible if the last four digits do not match one of the cards associated with their accounts.
    const account = rewardsAccounts.find(
      (account) =>
        account.lastFour === last4 ||
        account.lastFourVirtualCardNumbers?.includes(last4) ||
        account.discoverNetworkFlipInfo?.transferFromCardNumberLastFour ===
          last4,
    );
    let matchingRewardsAccount = account;

    const isAddingVCNPaymentMethod = !!(
      matchingRewardsAccount?.lastFourVirtualCardNumbers &&
      matchingRewardsAccount?.lastFourVirtualCardNumbers?.includes(last4)
    );

    // TODO: bad practice, remove this in favor of real test accounts when we have them
    const isTestCard =
      window.__mclean_env__.ENV !== "production" &&
      TEST_CARD_LAST_FOURS.includes(last4);
    if (isTestCard) {
      matchingRewardsAccount = rewardsAccounts[0];
    }

    if (!!matchingRewardsAccount || isTestCard || isCorpTenant(config.TENANT)) {
      verifyPaymentMethod(
        { token },
        matchingRewardsAccount?.accountReferenceId!,
        isAddingVCNPaymentMethod,
      );
    } else {
      setIsNotCapOneAccount(true);
      setOpenErrorPaymentModal(true);
    }
  };

  const handleCloseErrorPopup = () => {
    isNotCapOneAccount && setIsNotCapOneAccount(false);
    setOpenErrorPaymentModal(false);
  };

  const renderCheckoutPaymentForm = (isOpen: boolean) => {
    const isLoading = verifyPaymentMethodCallState === CallState.InProcess;
    return (
      <CheckoutPaymentForm
        loading={isLoading}
        loadingEl={<PaymentAddingElement />}
        onSubmit={(token, last4, prefillData) => {
          handleOnAddPaymentMethod(token, last4);
          setPaymentFormPrefillData(prefillData);
        }}
        saveLabel="Save"
        onError={(errors) => {
          setTokenizeErrors(errors);
          setOpenErrorPaymentModal(true);
        }}
        spreedlyEnvironmentKey={config.spreedlyEnvironmentKey}
        isMobile={matchesMobile}
        className="b2b"
        tenant={config.TENANT}
        prefillData={!isLoading ? paymentFormPrefillData : null}
        isOpen={isOpen}
      />
    );
  };

  const canUseAllCardsExperiment = getExperimentVariant(
    expState.experiments,
    ALL_CARDS_IN_PHC,
  );

  const canUseAllCards = useMemo(
    () => canUseAllCardsExperiment === AVAILABLE,
    [canUseAllCardsExperiment],
  );

  const vcnEnablement = getExperimentVariant(
    expState.experiments,
    VCN_ENABLEMENT,
  );
  const isVCNEnabled = useMemo(
    () => vcnEnablement === AVAILABLE,
    [vcnEnablement],
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1,
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1],
  );

  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT,
    ) === AVAILABLE;

  const isLifestyleOnlyCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
      AVAILABLE && !isPremierCollectionEnabled;

  const travelSaleVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS,
  );
  const isTravelSaleEnabled = travelSaleVariant !== CONTROL;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const allowAnyCards = useExperimentIsVariant(
    "c1-corporate-credit-card-validation",
    "any-spark",
  );

  const rewardsSelectedNeeded =
    !isPayLater &&
    (canRedeemRewards || isCaponeTenant(config.TENANT)
      ? selectedRewardsPaymentAccountId === undefined
      : false);

  const isPaymentMethodSelectDisabled =
    selectedRewardsPaymentAccount?.isTiered ||
    !isCreditCardPaymentRequired ||
    !totalCreditCardPaymentRequired ||
    rewardsSelectedNeeded;

  const eligiblePaymentTypesBannerText =
    PC_PAYMENT_TYPES_ELIGIBILITY_BANNER_TEXT(
      rewardsAccounts,
      allowAnyCards,
      paymentMethods,
      selectedLodging?.lodgingCollection === LodgingCollectionEnum.Lifestyle ||
        selectedHome?.listing.listingCollection ===
          ListingCollectionEnum.Lifestyle,
      isLifestyleOnlyCollectionEnabled,
      isLifestyleEligible,
    );

  const isNonFinancialUser = corpSessionInfo?.corporateInfo?.cap1Role === "NFU";

  const paymentMethodWorkflowTitles = () => ({
    addPaymentCta:
      rewardsAccounts.length === 1 ? (
        <Box
          className={clsx(
            "add-payment-cta-container",
            {
              mobile: matchesMobile,
              disabled: disabled || isPaymentMethodSelectDisabled,
            },
            className,
          )}
        >
          <Typography variant="body1">
            {paymentTextConstants.CTA_SINGLE_ACCOUNT_ADD_YOUR_TEXT}
            <b className="card-name">{rewardsAccounts[0].productDisplayName}</b>
            {paymentTextConstants.CTA_SINGLE_ACCOUNT_CREDIT_CARD_TEXT}
          </Typography>
          {rewardsAccounts[0].earn.hotelsMultiplier &&
          rewardsAccounts[0].earn.hotelsMultiplier > 0 ? (
            <Chip
              label={`Earn ${rewardsAccounts[0].earn.hotelsMultiplier}X on hotels`}
              className={clsx("earn-chip", {
                enabled: !isPaymentMethodSelectDisabled,
              })}
            />
          ) : null}
        </Box>
      ) : (
        paymentTextConstants.ADD_PAYMENT_METHOD_CTA_MULTIPLE_ACCOUNTS
      ),
    addPaymentModalTitle: paymentTextConstants.ADD_PAYMENT_METHOD_MODAL_TITLE,
    addAdditionalPaymentCta:
      paymentTextConstants.ADD_ADDITIONAL_PAYMENT_METHOD_CTA,
    backToCardSelectionCta: paymentTextConstants.BACK_TO_CARD_SELECTION_CTA,
    paymentFormHeader: (cardName: string) => (
      <SanitizedHtml
        as={Typography}
        variant="h4"
        html={paymentTextConstants.ADD_PAYMENT_FORM_HEADER_TEXT(cardName)}
      />
    ),
    cardEndingIn: paymentTextConstants.CARD_ENDING_IN_TEXT,
    addPaymentBannerText: !canUseAllCards
      ? eligiblePaymentTypesBannerText
      : undefined,
  });

  const getParadiseCardMessage = () =>
    rewardsAccounts.find((acct) =>
      acct.productDisplayName.toLowerCase().includes("paradise"),
    ) ? (
      <NotificationBanner
        className={clsx("paradise-banner-notification", {
          mobile: matchesMobile,
        })}
        label="Bookings made with your Paradise card through Capital One Travel cannot be serviced by concierge services. If you’d like concierge to manage your booking, book in the Capital One concierge app."
        severity={BannerSeverity.NOTICE}
      />
    ) : null;

  const primaryButton = {
    buttonText: paymentTextConstants.EDIT_PAYMENT_METHOD,
    onClick: () => {
      handleCloseErrorPopup();
    },
    defaultStyle: "h4r-secondary",
  };
  const secondaryButton = {
    buttonText: paymentTextConstants.TRY_AGAIN,
    onClick: () => {
      handleCloseErrorPopup();
    },
    defaultStyle: "h4r-primary",
    buttonWrapperClassName: "b2b",
  };

  const rewardsTitleId = createElementId("rewardsTitle");
  const rewardsSubtitleId = createElementId("rewardsSubtitle");

  const renderPaymentStepTitle = () => {
    const isCapone = isCaponeTenant(config.TENANT);

    if (isPayLater) {
      return matchesMobile
        ? PAYMENT_STEP_PAY_LATER_TITLE_MOBILE
        : PAYMENT_STEP_PAY_LATER_TITLE;
    }

    if (matchesMobile) {
      return paymentTextConstants.MOBILE_PAYMENT_STEP_TITLE(
        canRedeemRewards || isCapone,
      );
    }

    return paymentTextConstants.PAYMENT_STEP_TITLE(
      hasPayLater && isCapone ? 3 : 2,
      canRedeemRewards || isCapone,
    );
  };

  const renderPaymentStepSubtitle = () => {
    if (isPayLater) {
      return PAYMENT_STEP_PAY_LATER_SUBTITLE;
    }
    return isCreditAndOfferStackingExperimentV1
      ? paymentTextConstants.PAYMENT_CARD_SUBTITLE_WITH_CREDITS_AND_OFFERS
      : matchesMobile
      ? paymentTextConstants.MOBILE_PAYMENT_STEP_SUBTITLE(
          canRedeemRewards || isCaponeTenant(config.TENANT),
        )
      : paymentTextConstants.PAYMENT_STEP_SUBTITLE(
          canRedeemRewards || isCaponeTenant(config.TENANT),
        );
  };

  const renderPaymentStep = () => (
    <Box
      className={clsx("payment-methods-container", {
        mobile: matchesMobile,
        xmod: isXmodRedesign,
        disabled,
        redesigned: isVesper,
      })}
    >
      <Typography
        className="step-title"
        variant={matchesMobile && !isXmodRedesign ? "h4" : "h2"}
      >
        {renderPaymentStepTitle()}
      </Typography>
      <Box className="payment-step-subtitle-wrapper">
        <Typography
          variant="body2"
          className={clsx("payment-step-subtitle-text", {
            vesper: isVesper,
          })}
        >
          {renderPaymentStepSubtitle()}
        </Typography>
        <Tooltip
          content={
            <Typography className="payment-earn-info-popover-content">
              {paymentTextConstants.PAYMENT_EARN_INFO_TOOLTIP_TEXT}
            </Typography>
          }
          triggerClassName="payment-earn-info-btn"
          title="Rewards & Payment"
        >
          <Icon name={IconName.InfoCircle} />
        </Tooltip>
      </Box>
      {canRedeemRewards && !isPayLater && (
        <>
          {ineligibleRewardsAccounts.length > 0 && (
            <NotificationBanner
              className="authorized-users-rewards-banner"
              label={paymentTextConstants.INELIGIBLE_ACCOUNTS_NOTICE(
                rewardsAccounts,
              )}
              severity={BannerSeverity.NOTICE}
              tooltip={
                rewardsAccounts.length === ineligibleRewardsAccounts.length
                  ? {
                      label: paymentTextConstants.INELIGIBLE_ACCOUNTS_TOOLTIP(
                        ineligibleRewardsAccounts,
                      ),
                      icon: IconName.InfoCircle,
                    }
                  : undefined
              }
            />
          )}
          {!canUseAllCards && eligiblePaymentTypesBannerText && (
            <NotificationBanner
              className="pc-eligible-cards-banner"
              content={eligiblePaymentTypesBannerText}
              severity={BannerSeverity.NOTICE}
            />
          )}
          <Divider className="payment-methods-container-divider" />
          {isCreditAndOfferStackingExperimentV1 && (
            <TravelWalletSelection
              disabled={disabled}
              isMobile={matchesMobile}
              isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
              isCreditAndOfferStackingExperimentV1={
                isCreditAndOfferStackingExperimentV1
              }
              isTravelSale={isTravelSaleEnabled}
            />
          )}{" "}
          {getParadiseCardMessage()}
          <Typography
            variant="h4"
            component="h3"
            className="rewards-account-title"
          >
            {paymentTextConstants.REWARDS_ACCOUNT_TITLE(config.TENANT)}
          </Typography>
          <SanitizedHtml
            as={Typography}
            variant="body2"
            html={paymentTextConstants.REWARDS_ACCOUNT_SUBTITLE(config.TENANT)}
            className="rewards-accounts-subtitle"
          />
          {isRewardsDisabled && (
            <NotificationBanner
              className="rewards-disabled-banner"
              label={PREMIER_COLLECTION_REWARDS_DISABLED_BANNER_TEXT}
              severity={BannerSeverity.WARNING}
              icon={
                <Icon className="banner-icon" name={IconName.WarningAlert} />
              }
            />
          )}
          <RewardsSelection
            {...{
              disabled:
                disabled ||
                isRewardsDisabled ||
                (isCreditAndOfferStackingExperimentV1 &&
                  (isTravelCreditPaymentOnly ||
                    isStackedTravelWalletPaymentOnly ||
                    isTravelWalletOfferPaymentOnly)),
              rewardsTitleId,
              rewardsSubtitleId,
            }}
          />
        </>
      )}
      <Divider className="payment-methods-container-divider" />
      <Typography className="payment-method-title" component="h3" variant="h4">
        {paymentTextConstants.PAYMENT_METHOD_TITLE}
      </Typography>
      {getPaymentCardSubtitle(
        rewardsAccounts[0],
        corpSessionInfo?.corporateInfo?.businessName ?? "",
        isNonFinancialUser,
        config.TENANT,
        totalCreditCardPaymentRequired,
        !selectedPaymentMethodId,
        isPaymentMethodSelectDisabled,
      )}
      <B2BPaymentMethodSelectWorkflow
        allowAnyCards={allowAnyCards || isNonFinancialUser}
        errorModalOpen={openErrorPaymentModal}
        rewardsAccounts={rewardsAccounts ?? []}
        savedPayments={paymentMethods ?? []}
        selectedPaymentHopperId={selectedPaymentMethodId}
        disabled={
          isPaymentMethodSelectDisabled ||
          disabled ||
          hasError ||
          (isCreditAndOfferStackingExperimentV1 &&
            (isTravelCreditPaymentOnly ||
              isStackedTravelWalletPaymentOnly ||
              isTravelWalletOfferPaymentOnly))
        }
        selectPaymentMethod={(paymentId, rewardsAccount) => {
          setSelectedPaymentMethodId({
            paymentMethodId: paymentId,
            accountId: rewardsAccount?.accountReferenceId,
          });
          fetchProductToEarn();
        }}
        removePaymentMethod={(paymentId: string) => {
          deletePaymentMethod({ paymentId });
        }}
        titles={paymentMethodWorkflowTitles()}
        renderCheckoutPaymentForm={renderCheckoutPaymentForm}
        isMobile={matchesMobile}
        product={
          selectedHome
            ? TravelProductEnum.Homes
            : selectedLodging?.lodgingCollection ===
              LodgingCollectionEnum.Lifestyle
            ? TravelProductEnum.LifestyleCollection
            : TravelProductEnum.PremierCollection
        }
        loading={verifyPaymentMethodCallState === CallState.InProcess}
        buttonClassName="b2b"
        fullScreenWithBanner={matchesMobile}
        paymentMethodDisabled={shouldDisableCard(
          canUseAllCards,
          selectedLodging?.lodgingCollection ===
            LodgingCollectionEnum.Lifestyle ||
            selectedHome?.listing.listingCollection ===
              ListingCollectionEnum.Lifestyle,
          isLifestyleEligible,
        )}
        isVCNEnabled={isVCNEnabled}
        tenant={config.TENANT}
        showEarnValue
        earnValuesByRewardAcctId={earnValuesByRewardAcctId}
        corporateInfo={corpSessionInfo?.corporateInfo}
      />

      {isPayLater && (
        <Typography className="payment-pay-later-notes">
          {PAYMENT_PAY_LATER_NOTES}
        </Typography>
      )}
    </Box>
  );
  return (
    <>
      {listPaymentMethodCallState === CallState.InProcess ||
      deletePaymentMethodCallState === CallState.InProcess ? (
        <LoadingIndicator
          indicatorSize="small"
          indicator={B2BSpinner}
          message={
            listPaymentMethodCallState === CallState.InProcess
              ? `Fetching`
              : `Deleting`
          }
        />
      ) : (
        renderPaymentStep()
      )}
      <GenericInfoPopup
        open={openErrorPaymentModal}
        image={
          <Icon
            className="error-icon"
            name={
              isNotCapOneAccount
                ? IconName.ErrorState
                : IconName.UnableToProcess
            }
          />
        }
        title={
          isNotCapOneAccount
            ? paymentTextConstants.CAP_ONE_INVALID_CREDIT_CARD_TITLE
            : paymentTextConstants.UNABLED_TO_ADD_PAYMENT
        }
        subtitle={
          isNotCapOneAccount
            ? paymentTextConstants.CAP_ONE_INVALID_CREDIT_CARD_SUBTITLE(
                config.TENANT,
              )
            : tokenizeErrors.length > 0
            ? tokenizeErrors[0].message
            : paymentTextConstants.ADD_PAYMENT_AGAIN
        }
        buttons={
          isCorpTenant(config.TENANT)
            ? [primaryButton]
            : [primaryButton, secondaryButton]
        }
        isMobile={matchesMobile}
      />
    </>
  );
};
